/* eslint-disable react/no-unknown-property */
import React from 'react';
import {
  AvatarSkeleton, Card, Message, Text, TextSkeleton,
} from '@doveit/bricks';
import { CardHeaderProps } from '@doveit/bricks/dist/typings/components/card/composition';
import { Company } from '../../../providers/api/dtos';
import * as styles from './ViewCompanyPreview.style';
import useIsDevice from '../../../hooks/use-is-device/useIsDevice';
import useCompany from '../../hooks/use-company/useCompany';
import CompanyAvatar from '../../components/company-avatar/CompanyAvatar';

export type ViewCompanyPreviewProps = React.AriaAttributes & Pick<CardHeaderProps, 'title' | 'primaryActions'> & ({
  companyIdOrVat: NonNullable<Company['id'] | Company['vatNumber']>,
  company?: undefined,
} | {
  company: Company,
  companyIdOrVat?: undefined,
});

export const LOAD_COMPANY_DATA_ERROR_MESSAGE = 'Non è stato possibile caricare i dati della persona giuridica.';

const ViewCompanyPreview: React.FC<ViewCompanyPreviewProps> = ({
  companyIdOrVat,
  title = 'Persona giuridica',
  primaryActions,
  company: externalCompany,
  'aria-label': ariaLabel = 'view-company-preview',
  ...rest
}) => {
  const isMobile = useIsDevice('mobile');
  const { data: loadedCompany, isLoading: isCompanyLoading, error: companyError } = useCompany(companyIdOrVat);
  const company = externalCompany || loadedCompany;

  if (isCompanyLoading) {
    return (
      <Card>
        <Card.Content>
          <div
            aria-label={`${ariaLabel} (skeleton)`}
            css={styles.basic}
          >
            <div css={styles.info}>
              <AvatarSkeleton size={isMobile ? 'M' : 'L'} />
              <div css={styles.textInfo}>
                <div>
                  <TextSkeleton count={2} mainWordsInLine={1} width="12rem" />
                </div>
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>
    );
  }

  if (companyError) {
    return (
      <Message
        type="critical"
        message={LOAD_COMPANY_DATA_ERROR_MESSAGE}
      />
    );
  }

  return company && (
    <Card
      {...rest}
      aria-label={ariaLabel}
    >
      {primaryActions && (
        <Card.Header
          aria-label={`${ariaLabel} (header)`}
          title={title}
          primaryActions={primaryActions}
        />
      )}
      <Card.Content>
        <div css={styles.basic}>
          <div css={styles.info}>
            <CompanyAvatar size={isMobile ? 'M' : 'L'} />
            <div css={styles.textInfo}>
              <Text.H4
                aria-label={`${ariaLabel} (title)`}
                lineHeight="1"
                as="div"
              >
                {company.name}
              </Text.H4>
              <Text.Body
                lineHeight="1"
                as="div"
              >
                P. IVA {company.vatNumber}
              </Text.Body>
            </div>
          </div>
          <div css={styles.details}>
            <Text.BodySmall>{company.address.normalizedAddress}</Text.BodySmall>
          </div>
        </div>
      </Card.Content>
    </Card>
  );
};

export default ViewCompanyPreview;
